import {
  GetChannelDocument,
  type Community,
  type GetChannelQuery,
  UserInChannelDocument,
} from "@/generated/graphql";
import { defineStore } from "pinia";
import { computed, watchEffect, reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";
import {
  defaultChannelPermissions,
  getChannelPermissionsForMask,
  type IChannelPermissionsDescriptor,
} from "@f3/shared/permissions";
import { enhancedFetch } from "@/utils/enhanced-fetch";
import { useUserStore } from "./user.store";
import type {
  ChannelMetadata,
  ChannelAndUserInChannelMetadata,
  ExperienceMetadata,
} from "@/models";
import { useTrackingStore } from "@/stores/tracking.store";
import { useDumbStorage } from "@/composables/use-dumb-storage";
import { useSuperfanProduct } from "@/composables/use-superfan-product";
import { useQuery } from "@urql/vue";
import { getDefaultDomainChannel } from "@/utils/channel";
import { getExpectedChannelFromPath } from "@/router/app.router";

export interface IChannelStorePermissions extends IChannelPermissionsDescriptor {
  permissionMask: number | null;
}

async function fetchPermissionsForChannel(channelName: string) {
  const response = await enhancedFetch(
    `${import.meta.env.VITE_API_URL}/channel/${channelName}/permissions`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  const { data } = await response.json();
  return data as number;
}

export const useChannelStore = defineStore("channel", () => {
  const route = useRoute();
  const domain = window.location.host;
  const defaultAppChannel = getDefaultDomainChannel(domain);

  const channelSlug = computed(() => {
    const shouldRenderFanFest =
      route.fullPath === "/" && window.location.pathname === "/";

    if (shouldRenderFanFest) return defaultAppChannel;

    // Always return at least the default channel if route params aren't ready yet
    return (
      getExpectedChannelFromPath(
        window.location.pathname,
        route.params.channel as string,
      ) ?? null
    );
  });

  const hasChannelName = computed(() => channelSlug.value !== null);

  const lastLeaderboardUpdate = ref<number | null>(null);

  const channelQueryVariables = computed(() => ({
    channel_id: channelSlug.value!,
  }));

  const {
    data: result,
    fetching: loading,
    executeQuery: restart,
  } = useQuery({
    query: GetChannelDocument,
    variables: channelQueryVariables,
    pause: computed(() => !channelSlug.value),
  });

  const lastKnownChannel = ref<GetChannelQuery["channel"] | null>(null);
  watch(
    () => result.value?.channel,
    (ch) => {
      if (ch) lastKnownChannel.value = ch;
    },
  );

  const channel = computed<GetChannelQuery["channel"] | null>(() => {
    if (
      channelQueryVariables.value?.channel_id !== channelSlug.value ||
      channelSlug.value === null
    )
      return null;

    return result.value?.channel ?? lastKnownChannel.value ?? null;
  });

  const channelCommunities = computed(
    () => channel.value?.channel_communities ?? [],
  );

  const hasMultipleCommunities = computed(
    () => channelCommunities.value.length > 1,
  );

  const userStore = useUserStore();
  const trackingService = useTrackingStore();

  const monthLeaderboardUserRanking = computed({
    get() {
      return result.value?.monthLeaderboardUserEntry?.position ?? null;
    },

    set(value) {
      const resultValue = result.value;
      const entryResult = resultValue?.monthLeaderboardUserEntry;

      if (!resultValue || !entryResult || value === null) return;

      result.value = {
        ...resultValue,
        monthLeaderboardUserEntry: {
          ...entryResult,
          position: value,
        },
      };
    },
  });

  const channelToPermissions = reactive(
    new Map<string, IChannelStorePermissions>(),
  );
  const permissions = computed<IChannelStorePermissions>(() => {
    if (channelSlug.value && channelToPermissions.has(channelSlug.value)) {
      return channelToPermissions.get(channelSlug.value)!;
    } else {
      return { ...defaultChannelPermissions, permissionMask: null };
    }
  });
  const hasFetchedPermissions = computed(() =>
    userStore.isAuthenticated
      ? channelToPermissions.has(channelSlug.value ?? "")
      : // Unauthorized users get the default permissions
        // so we can consider them as having fetched permissions
        true,
  );

  // Clear permissions when user logs out
  watch(
    () => userStore.isAuthenticated,
    (value, oldValue) => {
      if (!value && oldValue) {
        channelToPermissions.clear();
      }
    },
    { flush: "sync" },
  );

  watchEffect(() => {
    const channelNameValue = channelSlug.value;
    const isAuthenticated = userStore.isAuthenticated;

    if (
      isAuthenticated &&
      channelNameValue !== null &&
      !channelToPermissions.has(channelNameValue)
    ) {
      fetchPermissionsForChannel(channelNameValue).then((permissions) => {
        channelToPermissions.set(channelNameValue, {
          ...getChannelPermissionsForMask(permissions),
          permissionMask: permissions,
        });
      });
    }
  });

  async function refreshPermissions() {
    const permissions = await fetchPermissionsForChannel(channelSlug.value!);
    channelToPermissions.set(channelSlug.value!, {
      ...getChannelPermissionsForMask(permissions),
      permissionMask: permissions,
    });
  }

  const channelCapabilities = computed(
    () => channel.value?.capabilities ?? null,
  );

  const channelMemberships = computed(() => {
    const list = [...(result.value?.channelMemberships ?? [])];
    list.sort((a, b) => a.permission_mask - b.permission_mask);
    return list;
  });

  const channelSections = computed(() => {
    return result.value?.channelSections ?? [];
  });

  // Pinned shows and threads
  const pinnedShowIds = ref<string[]>([]);
  const pinnedThreadIds = ref<string[]>([]);

  // Sync pinned IDs from query result
  watch(
    () => result.value,
    (newResult) => {
      if (newResult?.getChannelShowPins) {
        pinnedShowIds.value = newResult.getChannelShowPins;
      }
      if (newResult?.getChannelThreadPins) {
        pinnedThreadIds.value = newResult.getChannelThreadPins;
      }
    },
    { immediate: true },
  );

  function setPinnedShowIds(ids: string[]) {
    pinnedShowIds.value = ids;
  }

  function setPinnedThreadIds(ids: string[]) {
    pinnedThreadIds.value = ids;
  }

  function addPinnedShowId(showId: string) {
    // Since only one show can be pinned at a time, replace the array
    pinnedShowIds.value = [showId];
  }

  function removePinnedShowId(showId: string) {
    pinnedShowIds.value = pinnedShowIds.value.filter((id) => id !== showId);
  }

  function addPinnedThreadId(threadId: string) {
    // Since only one thread can be pinned at a time, replace the array
    pinnedThreadIds.value = [threadId];
  }

  function removePinnedThreadId(threadId: string) {
    pinnedThreadIds.value = pinnedThreadIds.value.filter(
      (id) => id !== threadId,
    );
  }

  const gatingExperience = ref<ExperienceMetadata | null>(null);

  function $setGatingExperience(trigger: ExperienceMetadata | null): void {
    gatingExperience.value = trigger;
  }

  const channelMetadata = computed<ChannelMetadata>(() => ({
    channelName: channelSlug.value ?? "",
    channelId: channel.value?.id,
    channelCommunityIds: userCommunities.value.length
      ? userCommunities.value.map((c) => c.id)
      : undefined,
    channelCommunityNames: userCommunities.value.length
      ? userCommunities.value.map((c) => c.name)
      : undefined,
  }));

  const channelUserMetadata = computed(
    (): ChannelAndUserInChannelMetadata => ({
      ...channelMetadata.value,
      channelId: channel.value?.id,
      channelCommunityIds: userCommunities.value.length
        ? userCommunities.value.map((c) => c.id)
        : undefined,
      channelCommunityNames: userCommunities.value.length
        ? userCommunities.value.map((c) => c.name)
        : undefined,
      channelRole: userRole.value,
      channelMembershipType:
        Number(userMembership.value?.price_tag) > 0 ? "paid" : "free",
      channelMembershipName: userMembership.value?.name,
      channelMembershipPriceTag: userMembership.value?.price_tag ?? undefined,
      channelPoints: userPoints?.value ?? undefined,
      channelEmailNotificationsEnabled:
        channel.value?.email_notifications_enabled,
    }),
  );

  const channelEmailNotificationsEnabled = computed(
    () => channel.value?.email_notifications_enabled,
  );

  const isHost = computed(() => permissions.value.produceShows);
  const hasMembership = computed(
    () => Number(userMembership.value?.price_tag) > 0,
  );
  const isVip = hasMembership; // backwards compat alias
  const isSuperfan = computed(
    () => userInChannelResult.value?.userInChannel?.is_superfan === true,
  );
  const isFreeMember = computed(
    () => userRole.value === "Fan" && !hasMembership.value,
  );

  function $reset() {
    restart();
  }

  const { data: userInChannelResult, executeQuery: refetchUserInChannel } =
    useQuery({
      query: UserInChannelDocument,
      variables: computed(() => ({ channel_id: channelSlug.value! })),
      pause: computed(() => !channelSlug.value),
    });

  const { hasSuperfanProduct } = useSuperfanProduct({
    enabled: () => channelCapabilities.value?.memberships_enabled ?? false,
  });

  const refreshUserInChannel = async () => {
    await refetchUserInChannel();
  };

  const userCommunities = computed<Community[]>(() => {
    if (!userStore.isAuthenticated) {
      return [];
    }
    const list = userInChannelResult.value?.userInChannel?.communities ?? [];

    return list;
  });

  const userRole = computed(() =>
    userStore.isAuthenticated
      ? (userInChannelResult.value?.userInChannel?.role ?? "Fan")
      : undefined,
  );
  const userMembership = computed(
    () => userInChannelResult.value?.userInChannel?.membership,
  );
  const userPoints = computed(
    () => userInChannelResult.value?.userInChannel?.points,
  );
  const userHasToSetCommunities = computed<boolean>(
    () =>
      userStore.isAuthenticated &&
      userInChannelResult.value?.userInChannel != null &&
      !userInChannelResult.value?.userInChannel?.follows_any_community,
  );

  const temporarySelectedCommunity = ref<string | null>(null);
  const persistentSelectedCommunity = useDumbStorage<string | null>(
    computed(
      () => `${channelSlug.value ?? defaultAppChannel}:selectedCommunity`,
    ),
    null,
    {
      serializer: {
        read: (value) => value,
        write: (value) => value,
      },
    },
  );

  const selectedCommunity = computed({
    get() {
      return (
        temporarySelectedCommunity.value ?? persistentSelectedCommunity.value
      );
    },
    set(value) {
      temporarySelectedCommunity.value = null;
      persistentSelectedCommunity.value = value;
    },
  });

  function resetCommunitySelection() {
    selectedCommunity.value = null;
  }

  async function updateSelectedCommunity(id: string) {
    selectedCommunity.value = id;
  }

  async function setTemporarySelectedCommunity(id: string) {
    temporarySelectedCommunity.value = id;
  }

  function toggleSelectedCommunity(communityId: string) {
    selectedCommunity.value =
      selectedCommunity.value === communityId ? null : communityId;
  }

  const isLeaderboardDisabled = computed(() => false);

  const isAsMonacoFanToken = computed(
    () => channelSlug.value === "asmonacofantoken",
  );

  const featureFlagsResult = computed(() => {
    const res = result.value?.resolvedFeatureFlags;
    return res && "flags" in res ? res.flags : null;
  });

  // Refresh feature flags when channel or auth state changes
  watch(
    () => userStore.isAuthenticated,
    async () => {
      if (channelSlug.value) {
        featureFlagsLoaded.value = false;
        await restart();
      }
    },
  );

  // ============================================================================
  // Feature Flags
  // ============================================================================

  const featureFlags = reactive(
    new Map<string, boolean | string | number | object>(),
  );
  const featureFlagsLoaded = ref(false);

  // Update feature flags map when query results change
  watch(
    () => featureFlagsResult.value,
    (flags) => {
      if (flags) {
        featureFlags.clear();
        for (const flag of flags) {
          if (!flag.is_enabled) continue;
          featureFlags.set(flag.key, flag.value.value);
        }
        featureFlagsLoaded.value = true;
      }
    },
    { immediate: true },
  );

  /**
   * Get a feature flag value by key.
   * Returns the parsed value or undefined if not found.
   */
  function getFeatureFlag<T = unknown>(key: string, defaultValue: T): T {
    const flag = featureFlags.get(key);
    if (!flag) return defaultValue;
    return flag as T;
  }

  /**
   * Refresh feature flags from the server.
   */
  async function refreshFeatureFlags(): Promise<void> {
    await restart();
  }

  watch(
    () => userStore.isAuthenticated,
    async (isAuthenticated) => {
      if (isAuthenticated) {
        await refreshUserInChannel();
      } else {
        userInChannelResult.value = undefined;
      }
    },
  );

  watch(
    () => channelMetadata.value,
    (value) => {
      trackingService.identifyChannel(value);
    },
  );

  watch(
    () => channelUserMetadata.value,
    (value) => {
      const user = userStore.user;
      if (!user) return;
      console.log(
        "[ChannelStore] Tracking service: Identifying user",
        user,
        value,
      );
      trackingService.identifyUser(user, value);
    },
  );

  return {
    channel,
    channelSlug,
    channelMemberships,
    channelSections,
    loadingChannel: loading,
    permissions,
    hasChannelName,
    channelCapabilities,
    hasFetchedPermissions,
    channelCommunities,
    userCommunities,
    userRole,
    userMembership,
    userPoints,
    userHasToSetCommunities,
    refreshUserInChannel,
    refreshPermissions,
    monthLeaderboardUserRanking,
    refetchMonthLeaderboardUserRanking: restart,
    gatingExperience,
    hasMultipleCommunities,
    selectedCommunity,
    lastLeaderboardUpdate,
    isVip,
    hasMembership,
    isSuperfan,
    hasSuperfanProduct,
    isHost,
    isFreeMember,
    channelEmailNotificationsEnabled,
    isLeaderboardDisabled,
    isAsMonacoFanToken,

    resetCommunitySelection,
    updateSelectedCommunity,
    setTemporarySelectedCommunity,
    toggleSelectedCommunity,
    $setGatingExperience,
    $reset,

    // Feature Flags
    featureFlags,
    featureFlagsLoaded,
    getFeatureFlag,
    refreshFeatureFlags,

    // Pinned shows and threads
    pinnedShowIds,
    pinnedThreadIds,
    setPinnedShowIds,
    setPinnedThreadIds,
    addPinnedShowId,
    removePinnedShowId,
    addPinnedThreadId,
    removePinnedThreadId,
  };
});
